import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { ChevronRight, Search, TextQuote, X } from 'lucide-react';
import CompactSelectPicker from './CompactSelectPicker';
import Tooltip from './Tooltip';
import {
  buildSchemaIndex,
  formatGraphQLType,
  getArgEnumValues,
  getExplorerChildFields,
  getFieldArgs,
  getFieldCheckboxState,
  getFieldOutputTypeName,
  getInputObjectFields,
  getQueryRootFields,
  getTypeClassName,
  isBooleanArg,
  isEnumArg,
  isExpandableField,
  isInputObjectExpandable,
  isScalarArg,
  searchSchemaExplorer,
  unwrapGraphQLType,
} from '../../utils/graphqlSchema';


const SHOW_DESCRIPTIONS_KEY = 'apiboo.graphql.schema.showDescriptions';
const EMPTY_SELECTED_PATHS = new Set();

function HighlightMatch({ text, filter }) {
  if (!text) return null;
  if (!filter) return text;

  const lower = text.toLowerCase();
  const idx = lower.indexOf(filter);
  if (idx === -1) return text;

  return (
    <>
      {text.slice(0, idx)}
      <mark className="gql-schema-match">{text.slice(idx, idx + filter.length)}</mark>
      {text.slice(idx + filter.length)}
    </>
  );
}

const SchemaTypeLabel = memo(function SchemaTypeLabel({ type, filter }) {
  const label = formatGraphQLType(type);
  if (!label) return null;
  return (
    <span className={`gql-schema-type-hint ${getTypeClassName(type)}`.trim()}>
      <HighlightMatch text={label} filter={filter} />
    </span>
  );
});

const SchemaDescription = memo(function SchemaDescription({ text, filter, showDescriptions }) {
  if (!showDescriptions || !text?.trim()) return null;
  return (
    <div className="gql-schema-description">
      <HighlightMatch text={text.trim()} filter={filter} />
    </div>
  );
});

const SchemaArgInput = memo(function SchemaArgInput({
  arg,
  schemaIndex,
  path,
  value,
  onArgValueChange,
}) {
  const enumValues = useMemo(
    () => (isEnumArg(arg) ? getArgEnumValues(arg, schemaIndex) : []),
    [arg, schemaIndex]
  );
  const enumOptions = useMemo(
    () => [
      { value: '', label: 'Select…' },
      ...enumValues.map((name) => ({ value: name, label: name })),
    ],
    [enumValues]
  );
  const booleanOptions = useMemo(
    () => [
      { value: '', label: 'Select…' },
      { value: 'true', label: 'true' },
      { value: 'false', label: 'false' },
    ],
    []
  );

  if (enumValues.length > 0) {
    return (
      <CompactSelectPicker
        value={value}
        onChange={(next) => onArgValueChange?.(path, next)}
        options={enumOptions}
        className="auth-picker gql-schema-arg-picker"
        menuMinWidth={140}
        aria-label={`${arg.name} argument value`}
      />
    );
  }

  if (isBooleanArg(arg)) {
    return (
      <CompactSelectPicker
        value={value}
        onChange={(next) => onArgValueChange?.(path, next)}
        options={booleanOptions}
        className="auth-picker gql-schema-arg-picker"
        menuMinWidth={100}
        aria-label={`${arg.name} argument value`}
      />
    );
  }

  return (
    <input
      type="text"
      className="gql-schema-arg-input"
      placeholder="Enter value"
      value={value}
      onChange={(e) => onArgValueChange?.(path, e.target.value)}
      aria-label={`${arg.name} argument value`}
    />
  );
});

const SchemaInputFieldNode = memo(function SchemaInputFieldNode({
  field,
  path,
  filter,
  visiblePaths,
  showDescriptions,
}) {
  if (visiblePaths && !visiblePaths.has(path)) return null;

  return (
    <div className="gql-schema-node">
      <div className="gql-schema-row gql-schema-row--input">
        <span className="gql-schema-expand-spacer" aria-hidden />
        <span className="gql-schema-arg-badge" aria-hidden>
          IN
        </span>
        <span className="gql-schema-field-name gql-schema-field-name--arg">
          <HighlightMatch text={field.name} filter={filter} />
        </span>
        <SchemaTypeLabel type={field.type} filter={filter} />
      </div>
      <SchemaDescription text={field.description} filter={filter} showDescriptions={showDescriptions} />
    </div>
  );
});

const SchemaArgNode = memo(function SchemaArgNode({
  arg,
  schemaIndex,
  path,
  expandedPaths,
  onExpand,
  selectedArgPaths,
  onArgToggle,
  argValues,
  onArgValueChange,
  filter,
  visiblePaths,
  showDescriptions,
}) {
  const scalarArg = isScalarArg(arg);
  const expandable = !scalarArg && isInputObjectExpandable(arg.type, schemaIndex);
  const expanded = expandedPaths.has(path);
  const inner = unwrapGraphQLType(arg.type);
  const inputFields = useMemo(
    () => (expandable ? getInputObjectFields(inner?.name, schemaIndex) : []),
    [expandable, inner?.name, schemaIndex]
  );
  const visibleInputFields = useMemo(
    () => (visiblePaths
      ? inputFields.filter((field) => visiblePaths.has(`${path}.${field.name}`))
      : inputFields),
    [inputFields, path, visiblePaths]
  );
  const value = argValues?.[path] ?? '';
  const argChecked = selectedArgPaths?.has(path) ?? false;

  if (visiblePaths && !visiblePaths.has(path)) return null;

  if (scalarArg) {
    return (
      <div className="gql-schema-node">
        <div className="gql-schema-row gql-schema-row--arg gql-schema-row--arg-inline">
          <span className="gql-schema-expand-spacer" aria-hidden />
          <input
            type="checkbox"
            className="checkbox-brand gql-schema-checkbox gql-schema-checkbox--arg"
            checked={argChecked}
            onChange={() => onArgToggle?.(path)}
            aria-label={`Include ${arg.name} argument`}
          />
          <span className="gql-schema-field-name gql-schema-field-name--arg">
            <HighlightMatch text={arg.name} filter={filter} />
          </span>
          <SchemaTypeLabel type={arg.type} filter={filter} />
          {argChecked ? (
            <SchemaArgInput
              arg={arg}
              schemaIndex={schemaIndex}
              path={path}
              value={value}
              onArgValueChange={onArgValueChange}
            />
          ) : null}
          <span className="gql-schema-arg-badge" aria-hidden>
            ARG
          </span>
        </div>
        <SchemaDescription text={arg.description} filter={filter} showDescriptions={showDescriptions} />
      </div>
    );
  }

  return (
    <div className="gql-schema-node">
      <div className="gql-schema-row gql-schema-row--arg gql-schema-row--arg-header">
        {expandable ? (
          <button
            type="button"
            className={`gql-schema-expand${expanded ? ' gql-schema-expand--open' : ''}`}
            onClick={() => onExpand(path)}
            aria-label={expanded ? 'Collapse argument' : 'Expand argument'}
            tabIndex={-1}
          >
            <ChevronRight size={12} />
          </button>
        ) : (
          <span className="gql-schema-expand-spacer" aria-hidden />
        )}
        <span className="gql-schema-field-name gql-schema-field-name--arg">
          <HighlightMatch text={arg.name} filter={filter} />
        </span>
        <SchemaTypeLabel type={arg.type} filter={filter} />
        <span className="gql-schema-arg-badge" aria-hidden>
          ARG
        </span>
      </div>
      <SchemaDescription text={arg.description} filter={filter} showDescriptions={showDescriptions} />
      {expandable && expanded && visibleInputFields.length > 0 && (
        <div className="gql-schema-scope">
          {visibleInputFields.map((field) => (
            <SchemaInputFieldNode
              key={`${path}.${field.name}`}
              field={field}
              path={`${path}.${field.name}`}
              filter={filter}
              visiblePaths={visiblePaths}
              showDescriptions={showDescriptions}
            />
          ))}
        </div>
      )}
    </div>
  );
});

const SchemaFieldNode = memo(function SchemaFieldNode({
  field,
  schemaIndex,
  path,
  selectedPaths,
  expandedPaths,
  onToggle,
  onExpand,
  filter,
  selectedArgPaths,
  onArgToggle,
  argValues,
  onArgValueChange,
  visitedTypes,
  visiblePaths,
  showDescriptions,
}) {
  const checkboxRef = useRef(null);
  const name = field.name;
  const expandable = isExpandableField(field, schemaIndex, visitedTypes);
  const expanded = expandedPaths.has(path);
  const args = getFieldArgs(field);
  const childFields = useMemo(
    () => (expandable ? getExplorerChildFields(field, schemaIndex) : []),
    [expandable, field, schemaIndex]
  );
  const typeName = getFieldOutputTypeName(field);
  const childVisited = useMemo(
    () => (typeName ? new Set(visitedTypes).add(typeName) : visitedTypes),
    [typeName, visitedTypes]
  );

  const visibleArgs = useMemo(
    () => (visiblePaths
      ? args.filter((arg) => visiblePaths.has(`${path}.arg.${arg.name}`))
      : args),
    [args, path, visiblePaths]
  );

  const visibleChildren = useMemo(
    () => (visiblePaths
      ? childFields.filter((child) => visiblePaths.has(`${path}.${child.name}`))
      : childFields),
    [childFields, path, visiblePaths]
  );

  const checkboxState = useMemo(
    () => getFieldCheckboxState(field, schemaIndex, path, selectedPaths),
    [field, schemaIndex, path, selectedPaths]
  );
  const checked = checkboxState === 'checked';
  const indeterminate = checkboxState === 'indeterminate';

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate, checked]);

  if (visiblePaths && !visiblePaths.has(path)) return null;

  const showBody = expanded && (visibleArgs.length > 0 || visibleChildren.length > 0);

  return (
    <div className="gql-schema-node">
      <div className="gql-schema-row">
        {expandable || args.length > 0 ? (
          <button
            type="button"
            className={`gql-schema-expand${expanded ? ' gql-schema-expand--open' : ''}`}
            onClick={() => onExpand(path)}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            tabIndex={-1}
          >
            <ChevronRight size={12} />
          </button>
        ) : (
          <span className="gql-schema-expand-spacer" aria-hidden />
        )}
        <input
          ref={checkboxRef}
          type="checkbox"
          className="checkbox-brand gql-schema-checkbox"
          checked={checked}
          onChange={() => onToggle(path)}
          aria-label={`Include ${name}`}
        />
        <span className="gql-schema-field-name">
          <HighlightMatch text={name} filter={filter} />
        </span>
        <SchemaTypeLabel type={field.type} filter={filter} />
      </div>
      <SchemaDescription text={field.description} filter={filter} showDescriptions={showDescriptions} />
      {showBody && (
        <div className="gql-schema-scope">
          {visibleArgs.map((arg) => (
            <SchemaArgNode
              key={`${path}.arg.${arg.name}`}
              arg={arg}
              schemaIndex={schemaIndex}
              path={`${path}.arg.${arg.name}`}
              expandedPaths={expandedPaths}
              onExpand={onExpand}
              selectedArgPaths={selectedArgPaths}
              onArgToggle={onArgToggle}
              argValues={argValues}
              onArgValueChange={onArgValueChange}
              filter={filter}
              visiblePaths={visiblePaths}
              showDescriptions={showDescriptions}
            />
          ))}
          {visibleChildren.map((child) => (
            <SchemaFieldNode
              key={`${path}.${child.name}`}
              field={child}
              schemaIndex={schemaIndex}
              path={`${path}.${child.name}`}
              selectedPaths={selectedPaths}
              expandedPaths={expandedPaths}
              onToggle={onToggle}
              onExpand={onExpand}
              filter={filter}
              selectedArgPaths={selectedArgPaths}
              onArgToggle={onArgToggle}
              argValues={argValues}
              onArgValueChange={onArgValueChange}
              visitedTypes={childVisited}
              visiblePaths={visiblePaths}
              showDescriptions={showDescriptions}
            />
          ))}
        </div>
      )}
    </div>
  );
});

const GraphQLSchemaExplorer = ({
  schema,
  schemaStatus = 'idle',
  schemaError = null,
  selectedPaths = EMPTY_SELECTED_PATHS,
  onSelectedPathsChange,
  selectedArgPaths = EMPTY_SELECTED_PATHS,
  onArgToggle,
  argValues = {},
  onArgValueChange,
}) => {
  const [expandedPaths, setExpandedPaths] = useState(() => new Set());
  const [filterText, setFilterText] = useState('');
  // ON by default: a schema's own words ("Books, optionally of one genre.") are the point of
  // an explorer, and they sat behind an unlabelled icon. The choice is remembered.
  const [showDescriptions, setShowDescriptions] = useState(() => {
    try { return localStorage.getItem(SHOW_DESCRIPTIONS_KEY) !== 'false'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(SHOW_DESCRIPTIONS_KEY, String(showDescriptions)); } catch { /* private mode */ }
  }, [showDescriptions]);
  const preSearchExpandedRef = useRef(null);

  const schemaIndex = useMemo(
    () => (schema ? buildSchemaIndex(schema) : new Map()),
    [schema]
  );

  const rootFields = useMemo(
    () => (schema ? getQueryRootFields(schema, schemaIndex) : []),
    [schema, schemaIndex]
  );

  useEffect(() => {
    if (!schema) {
      setExpandedPaths(new Set());
      setFilterText('');
      preSearchExpandedRef.current = null;
      return;
    }
    setExpandedPaths(new Set());
    setFilterText('');
    preSearchExpandedRef.current = null;
  }, [schema]);

  const filter = filterText.trim().toLowerCase();

  const filterResult = useMemo(
    () => (filter
      ? searchSchemaExplorer({ schemaIndex, rootFields, expandedPaths, filter })
      : null),
    [filter, schemaIndex, rootFields, expandedPaths]
  );

  const visiblePaths = filterResult?.visiblePaths ?? null;

  useEffect(() => {
    if (filter) {
      if (!preSearchExpandedRef.current) {
        preSearchExpandedRef.current = new Set(expandedPaths);
      }
      if (filterResult) {
        setExpandedPaths((prev) => {
          const next = new Set(filterResult.expandPaths);
          for (const path of prev) next.add(path);
          return next;
        });
      }
      return;
    }
    if (preSearchExpandedRef.current) {
      setExpandedPaths(new Set(preSearchExpandedRef.current));
      preSearchExpandedRef.current = null;
    }
  }, [filter, filterResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExpand = useCallback((path) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const handleToggle = useCallback(
    (path) => {
      onSelectedPathsChange?.((prev) => {
        const next = new Set(prev);
        if (next.has(path)) {
          next.delete(path);
        } else {
          next.add(path);
          setExpandedPaths((exp) => {
            const updated = new Set(exp);
            updated.add(path);
            return updated;
          });
        }
        return next;
      });
    },
    [onSelectedPathsChange]
  );

  const emptyVisited = useMemo(() => new Set(), []);

  const visibleRootFields = useMemo(() => {
    if (!visiblePaths) return rootFields;
    return rootFields.filter((field) => visiblePaths.has(field.name));
  }, [rootFields, visiblePaths]);

  const hasFilterMatches = !filter || (filterResult?.matchingPaths.size ?? 0) > 0;

  return (
    <div className="gql-schema-panel">
      <div className="gql-schema-panel-header">
        <div className="gql-editor-panel__label gql-schema-panel__label">Schema</div>
        <div className="gql-schema-toolbar">
          <Tooltip content={showDescriptions ? 'Hide descriptions' : 'Show descriptions'} side="bottom">
            <button
              type="button"
              className={`gql-schema-toolbar-btn${showDescriptions ? ' gql-schema-toolbar-btn--active' : ''}`}
              onClick={() => setShowDescriptions((prev) => !prev)}
              aria-pressed={showDescriptions}
              aria-label={showDescriptions ? 'Hide descriptions' : 'Show descriptions'}
            >
              <TextQuote size={13} aria-hidden />
            </button>
          </Tooltip>
          <div className="gql-schema-search-wrap">
            <div className="gql-schema-search-inner">
              <Search size={13} className="gql-schema-search-icon" aria-hidden />
              <input
                type="text"
                className="gql-schema-search"
                placeholder="Search fields…"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                disabled={!schema || schemaStatus === 'loading'}
                autoComplete="off"
              />
              {filterText ? (
                <button
                  type="button"
                  className="gql-schema-search-clear"
                  onClick={() => setFilterText('')}
                  aria-label="Clear search"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <X size={13} strokeWidth={2} aria-hidden />
                </button>
              ) : (
                <span className="gql-schema-search-clear-spacer" aria-hidden />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="gql-schema-tree panel-scroll">
        {schemaStatus === 'loading' && (
          <div className="gql-schema-message">Loading schema…</div>
        )}
        {schemaStatus === 'error' && (
          <div className="gql-schema-message gql-schema-message--error">
            {schemaError || 'Schema fetch failed'}
          </div>
        )}
        {schemaStatus === 'idle' && !schema && (
          <div className="gql-schema-message">Enter a URL and enable Auto Fetch</div>
        )}
        {schema && schemaStatus === 'ok' && rootFields.length === 0 && (
          <div className="gql-schema-message">No query fields found</div>
        )}
        {schema && schemaStatus === 'ok' && filter && !hasFilterMatches && (
          <div className="gql-schema-message">No fields match &ldquo;{filterText.trim()}&rdquo;</div>
        )}
        {schema && schemaStatus === 'ok' &&
          visibleRootFields.map((field) => (
            <SchemaFieldNode
              key={field.name}
              field={field}
              schemaIndex={schemaIndex}
              path={field.name}
              selectedPaths={selectedPaths}
              expandedPaths={expandedPaths}
              onToggle={handleToggle}
              onExpand={handleExpand}
              filter={filter}
              selectedArgPaths={selectedArgPaths}
              onArgToggle={onArgToggle}
              argValues={argValues}
              onArgValueChange={onArgValueChange}
              visitedTypes={emptyVisited}
              visiblePaths={visiblePaths}
              showDescriptions={showDescriptions}
            />
          ))}
      </div>
    </div>
  );
};

export default GraphQLSchemaExplorer;
